import { ValidatorFn, FormArray, AbstractControl } from '@angular/forms';
import isPlainObject from 'lodash/isPlainObject';
import omit from 'lodash/omit';

import { InfoPropertyComponent } from './info-property.component';
import { ContainerInfo, ContainerInfoValue } from '../../shared/models/container.model';

export type CustomFieldType = 'string' | 'number' | 'boolean' | 'array' | 'object';
export type CustomFieldValue = string | number | boolean;

export interface CustomField {
  type: CustomFieldType
  key: string
  value: CustomFieldValue | CustomField[]
}

export function parseInfo(info: ContainerInfo = {}): CustomField[] {
  return Object.entries(info)
    .sort((a, b) => a[0].toLowerCase() > b[0].toLowerCase() ? 1 : -1)
    .map(([key, value]) => parseInfoValue(key, value)) as CustomField[];
}

export function parseInfoValue(key: string, value: ContainerInfoValue): CustomField {
  if (Array.isArray(value)) {
    return { type: 'array', key, value: value.map((element, index) => parseInfoValue(String(index), element)) };
  } else if (isPlainObject(value)) {
    return { type: 'object', key, value: parseInfo(value as ContainerInfo) };
  } else if (typeof value === 'boolean') {
    return { type: 'boolean', key, value };
  } else if (typeof value === 'number') {
    return { type: 'number', key, value };
  } else {
    return { type: 'string', key, value: (value || '') as string };
  }
}

export function infoToJson(customFields: CustomField[]): ContainerInfo {
  const json: ContainerInfo = {};
  for (const field of customFields) {
    json[field.key] = infoValueToJson(field);
  }
  return json;
}

export function infoValueToJson(customField: CustomField): ContainerInfoValue {
  if (customField.type === 'object') {
    return infoToJson(customField.value as CustomField[]);
  } else if (customField.type === 'array') {
    return (customField.value as CustomField[]).map(field => infoValueToJson(field));
  } else if (customField.type === 'number' || customField.type === 'boolean') {
    return JSON.parse(customField.value as string);
  } else {
    return customField.value as ContainerInfoValue;
  }
}

export function validateInfo(info: ContainerInfo): boolean {
  return Object.entries(info).every(([key, value]) => {
    if (key === '') {
      return false;
    }
    if (isPlainObject(value)) {
      return validateInfo(value as ContainerInfo);
    }
    return true;
  });
}

export const invalidCharacterValidator: ValidatorFn = control => {
  if (control.pristine || !control.value) {
    return null;
  }
  // disallow Mongo (.$) or Pydantic (_) operators
  return /(^[$_])|[.]/.test(control.value) ? { invalidCharacter: true } : null;
};

export const duplicateKeyValidator: ValidatorFn = (array: FormArray) => {
  if (array.pristine) {
    return null;
  }
  const keys: { [key: string]: AbstractControl } = {};
  let duplicateKeys = false;
  for (const control of array.controls) {
    const key = control.get('key');
    if (key.value === '') {
      continue;
    }
    if (key.value in keys) {
      duplicateKeys = true;
      const dupe = keys[key.value];
      dupe.setErrors({
        duplicateKey: true,
      });
      dupe.markAsTouched();
      key.setErrors({
        duplicateKey: true,
      });
      key.markAsTouched();
    } else {
      keys[key.value] = key;
      if ('duplicateKey' in (key.errors ?? {})) {
        const errors = omit(key.errors, 'duplicateKey');
        key.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    }
  }
  return duplicateKeys ? { duplicateKeys: true } : null;
};

/**
 * Recursively expand info nodes if they contain invalid descendants.
 */
export function revealInvalid(nodes: Iterable<InfoPropertyComponent>): void {
  for (const node of nodes) {
    if ((node.fieldType === 'root' || node.isArrayOrObject) && node.invalid) {
      node.expanded = true;
      setTimeout(() => {
        // children of a collapsed node are not rendered
        // must defer until they are instantiated in the next render
        if (node.childNodes.length > 0) {
          revealInvalid(node.childNodes);
        } else if (node.formArray.length > 0) {
          revealInvalid([node]); // try again
        }
      }, 50);
    }
  }
}
